import { useGameStore } from '../../core/store';
import { computeIsMobile } from '../../core/useIsMobile';

/** Event-driven clip mixer — CoD-style buses, imported WAV/MP3 from /audio. */

type Bus = 'master' | 'ambience' | 'foley' | 'ui' | 'voice';
type Sfx =
  | 'footstep'
  | 'tackle'
  | 'core'
  | 'whoosh'
  | 'cheer'
  | 'tick'
  | 'goal'
  | 'netHit'
  | 'silence'
  | 'door'
  | 'popup'
  | 'lockerLight'
  | 'skillUnlock';

const CLIP_PATHS: Record<
  | Sfx
  | 'crowd'
  | 'crowdGoal'
  | 'gameIntro'
  | 'lockerBed'
  | 'tunnel'
  | 'tunnelBreath'
  | 'tunnelPitch'
  | 'pitch'
  | 'pitchAmbience'
  | 'exitBlackVo',
  string
> = {
  crowd: '/audio/crowd/stadium-loop.wav',
  crowdGoal: '/audio/custom_audio/crowd-celebration-goal.mp3',
  gameIntro: '/audio/ambience/game-intro.mp3',
  lockerBed: '/audio/ambience/locker-bed.mp3',
  tunnel: encodeURI('/audio/custom_audio/tunnel sound 2.mp3'),
  tunnelBreath: encodeURI('/audio/custom_audio/tunnel breathing.mp3'),
  // Distant pitch under the tunnel walk (ramps 0→1 with tunnel bed)
  tunnelPitch: encodeURI('/audio/custom_audio/football pitch sound .mp3'),
  // Music bed under the penalty sequence
  pitch: encodeURI('/audio/custom_audio/penalty taking bg.mp3'),
  // Soft stadium bed — sits under the music (quiet + slightly slow)
  pitchAmbience: encodeURI('/audio/custom_audio/football pitch sound .mp3'),
  exitBlackVo: encodeURI(
    '/audio/custom_audio/tunnel exit black screen voice over .mp3',
  ),
  footstep: '/audio/foley/footstep.wav',
  tackle: '/audio/foley/tackle.wav',
  door: '/audio/foley/door-open.mp3',
  core: '/audio/skill/core.wav',
  whoosh: '/audio/skill/whoosh.wav',
  cheer: '/audio/crowd/cheer.wav',
  tick: '/audio/ui/tick.wav',
  goal: '/audio/cinematic/goal-swell.wav',
  netHit: encodeURI('/audio/custom_audio/football hit net.mp3'),
  silence: '/audio/cinematic/silence-snap.wav',
  popup: encodeURI('/audio/custom_audio/pop up box audio.mp3'),
  lockerLight: encodeURI('/audio/custom_audio/dressing room light.mp3'),
  skillUnlock: encodeURI('/audio/custom_audio/skill unlocked.mp3'),
};

/** Global mix — raised so beds/SFX are clear at normal speaker levels. */
const MASTER_GAIN = 1.05;
const BUS_AMBIENCE = 0.85;
const BUS_FOLEY = 0.9;
const BUS_UI = 0.78;
const BUS_VOICE = 1.0;
const SFX_GAIN_MUL = 1.55;
const LOCKER_BED_GAIN = 0.92;
const GAME_INTRO_GAIN = 1.05;
const TUNNEL_BED_GAIN = 0.92;
/** Quiet distant pitch under the tunnel — full at mouth still stays under the bed. */
const TUNNEL_PITCH_GAIN = 0.32;
const PITCH_BED_GAIN = 0.88;
/** Skip the first 0.8s of `penalty taking bg.mp3`. */
const PITCH_BED_OFFSET_S = 0.8;
/** Play music only until 1:30 in the file (then stop — no loop). */
const PITCH_BED_END_S = 90;
/** Soft under-layer — never compete with BG music or netHit. */
const PITCH_AMBIENCE_GAIN = 0.22;
const PITCH_AMBIENCE_RATE = 0.86;
const TUNNEL_BREATH_GAIN = 4.5;
const CROWD_GAIN_MUL = 0.78;
/** Crowd roar after net hit — start at 1.5s in file; fade when trophy video starts. */
const CROWD_GOAL_OFFSET_S = 1.5;
/** ~35% quieter than the original 1.05 mix level. */
const CROWD_GOAL_GAIN = 0.4825;
const CROWD_GOAL_FADE_S = 0.85;
const POPUP_TIP_GAIN = 0.48;
const LOCKER_LIGHT_GAIN = 2.25;

let ctx: AudioContext | null = null;
const buffers = new Map<string, AudioBuffer>();
const buses: Partial<Record<Bus, GainNode>> = {};
let crowdGain: GainNode | null = null;
let crowdSource: AudioBufferSourceNode | null = null;
let lockerBedGain: GainNode | null = null;
let lockerBedSource: AudioBufferSourceNode | null = null;
let gameIntroGain: GainNode | null = null;
let gameIntroSource: AudioBufferSourceNode | null = null;
let tunnelGain: GainNode | null = null;
let tunnelSource: AudioBufferSourceNode | null = null;
let tunnelBreathGain: GainNode | null = null;
let tunnelBreathSource: AudioBufferSourceNode | null = null;
let tunnelPitchGain: GainNode | null = null;
let tunnelPitchSource: AudioBufferSourceNode | null = null;
let pitchGain: GainNode | null = null;
let pitchSource: AudioBufferSourceNode | null = null;
let pitchAmbienceGain: GainNode | null = null;
let pitchAmbienceSource: AudioBufferSourceNode | null = null;
let unlocked = false;
let crowdIntensity = 0.4;
let lockerBedStopping = false;
let tunnelStopping = false;
let tunnelBreathStopping = false;
let tunnelPitchStopping = false;
let pitchStopping = false;
let pitchAmbienceStopping = false;
/** Prevents overlapping async starts from cutting a live bed. */
let pitchBedStartLock = false;
let pitchAmbienceStartLock = false;
let gameIntroPlayed = false;
let lockerLightPlayed = false;
let lockerLightTimeout: number | null = null;
let exitBlackVoSource: AudioBufferSourceNode | null = null;
let exitBlackVoGain: GainNode | null = null;
let crowdGoalSource: AudioBufferSourceNode | null = null;
let crowdGoalGain: GainNode | null = null;
let crowdGoalPlayed = false;

function getCtx(): AudioContext {
  if (!ctx) ctx = new AudioContext();
  return ctx;
}

function ensureBuses() {
  const c = getCtx();
  if (buses.master) return;
  const master = c.createGain();
  master.gain.value = MASTER_GAIN;
  master.connect(c.destination);

  const make = (v: number) => {
    const g = c.createGain();
    g.gain.value = v;
    g.connect(master);
    return g;
  };

  buses.master = master;
  buses.ambience = make(BUS_AMBIENCE);
  buses.foley = make(BUS_FOLEY);
  buses.ui = make(BUS_UI);
  buses.voice = make(BUS_VOICE);
}

async function loadBuffer(url: string): Promise<AudioBuffer | null> {
  if (buffers.has(url)) return buffers.get(url)!;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const arr = await res.arrayBuffer();
    const buf = await getCtx().decodeAudioData(arr.slice(0));
    buffers.set(url, buf);
    return buf;
  } catch {
    return null;
  }
}

export async function unlockAudio() {
  ensureBuses();
  const c = getCtx();
  if (c.state === 'suspended') await c.resume();
  unlocked = true;
}

export async function preloadAudio() {
  ensureBuses();
  await Promise.all(Object.values(CLIP_PATHS).map((p) => loadBuffer(p)));
}

function synthFallback(kind: Sfx) {
  // Custom clips only — never invent a stand-in for netHit.
  if (kind === 'netHit') return;
  const c = getCtx();
  ensureBuses();
  const now = c.currentTime;
  if (kind === 'silence') {
    // Quick duck of ambience beds
    if (crowdGain) {
      crowdGain.gain.setTargetAtTime(0.001, now, 0.05);
    }
    if (lockerBedGain && !lockerBedStopping) {
      const cur = Math.max(lockerBedGain.gain.value, 0.0001);
      lockerBedGain.gain.cancelScheduledValues(now);
      lockerBedGain.gain.setValueAtTime(cur, now);
      lockerBedGain.gain.exponentialRampToValueAtTime(0.22, now + 0.08);
      lockerBedGain.gain.exponentialRampToValueAtTime(LOCKER_BED_GAIN, now + 0.9);
    }
    return;
  }
  const osc = c.createOscillator();
  const g = c.createGain();
  const bus =
    kind === 'cheer' || kind === 'footstep' || kind === 'goal'
      ? buses.ambience!
      : kind === 'tick'
        ? buses.ui!
        : buses.foley!;
  osc.type = kind === 'tackle' ? 'square' : kind === 'goal' ? 'sawtooth' : 'triangle';
  const f0 =
    kind === 'tackle'
      ? 80
      : kind === 'core'
        ? 660
        : kind === 'skillUnlock'
          ? 720
        : kind === 'cheer' || kind === 'goal'
          ? 400
          : 220;
  osc.frequency.setValueAtTime(f0, now);
  if (kind === 'goal') {
    osc.frequency.exponentialRampToValueAtTime(880, now + 0.6);
  }
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(
    kind === 'goal' ? 0.22 : 0.14,
    now + 0.02,
  );
  g.gain.exponentialRampToValueAtTime(0.0001, now + (kind === 'goal' ? 1.2 : 0.25));
  osc.connect(g);
  g.connect(bus);
  osc.start(now);
  osc.stop(now + (kind === 'goal' ? 1.3 : 0.3));
}

export function play(kind: Sfx, opts: { gain?: number; rate?: number } = {}) {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();
  const url = CLIP_PATHS[kind];
  const buf = buffers.get(url);
  if (!buf) {
    synthFallback(kind);
    return;
  }
  const c = getCtx();
  const src = c.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = opts.rate ?? 0.94 + Math.random() * 0.12;
  const g = c.createGain();
  g.gain.value =
    (opts.gain ?? 1) * SFX_GAIN_MUL * (0.9 + Math.random() * 0.15);
  const bus =
    kind === 'cheer' || kind === 'goal' || kind === 'lockerLight'
      ? buses.ambience!
      : kind === 'tick' ||
          kind === 'whoosh' ||
          kind === 'core' ||
          kind === 'skillUnlock' ||
          kind === 'silence' ||
          kind === 'popup'
        ? buses.ui!
        : kind === 'netHit'
          ? buses.foley!
          : buses.foley!;
  // Net hit should play at authored rate (no randomize).
  // Keep only the first ~0.9s of the impact — fade out the long ring.
  if (kind === 'netHit') {
    const NET_HIT_DUR = 0.9;
    const NET_HIT_FADE = 0.12;
    src.playbackRate.value = opts.rate ?? 1;
    const peak = (opts.gain ?? 1) * SFX_GAIN_MUL;
    const now = c.currentTime;
    g.gain.setValueAtTime(peak, now);
    g.gain.setValueAtTime(peak, now + Math.max(0, NET_HIT_DUR - NET_HIT_FADE));
    g.gain.exponentialRampToValueAtTime(0.0001, now + NET_HIT_DUR);
    src.connect(g);
    g.connect(bus);
    src.start(now);
    src.stop(now + NET_HIT_DUR + 0.02);
    return;
  }
  if (kind === 'skillUnlock') {
    src.playbackRate.value = opts.rate ?? 1;
    g.gain.value = (opts.gain ?? 1.2) * SFX_GAIN_MUL;
    src.connect(g);
    g.connect(bus);
    src.start();
    return;
  }
  src.connect(g);
  g.connect(bus);
  src.start();
}

/** Ensures the unlock clip is decoded, then plays (fixes first-board miss). */
export async function preloadSkillUnlock() {
  const url = CLIP_PATHS.skillUnlock;
  if (!buffers.has(url)) await loadBuffer(url);
}

export async function playSkillUnlock() {
  if (useGameStore.getState().muted) return;
  if (!unlocked) {
    try {
      await unlockAudio();
    } catch {
      /* soft */
    }
  }
  if (!unlocked) return;
  await preloadSkillUnlock();
  play('skillUnlock', { gain: 1.25, rate: 1 });
}

/**
 * Crowd celebration on net hit — plays `crowd-celebration-goal.mp3`
 * from 1.5s onward (parallel with pitch bed / netHit). Call
 * `fadeCrowdGoalCelebration()` when the trophy video appears.
 */
export async function playCrowdGoalCelebration(opts: { gain?: number } = {}) {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  if (crowdGoalPlayed) return;
  ensureBuses();

  const url = CLIP_PATHS.crowdGoal;
  const buf = buffers.get(url) ?? (await loadBuffer(url));
  if (!buf || crowdGoalPlayed) return;

  crowdGoalPlayed = true;
  const c = getCtx();
  const offset = Math.min(CROWD_GOAL_OFFSET_S, Math.max(0, buf.duration - 0.05));
  // Play the rest of the clip until we fade for the trophy reel
  const playDur = Math.max(0.05, buf.duration - offset);
  const peak = opts.gain ?? CROWD_GOAL_GAIN;

  try {
    crowdGoalSource?.stop();
  } catch {
    /* already ended */
  }
  crowdGoalSource = null;
  crowdGoalGain = null;

  const g = c.createGain();
  g.gain.value = peak;
  g.connect(buses.ambience!);
  crowdGoalGain = g;

  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = false;
  src.playbackRate.value = 1;
  src.connect(g);
  crowdGoalSource = src;

  const now = c.currentTime;
  src.start(now, offset, playDur);
  g.gain.setValueAtTime(peak, now);

  src.onended = () => {
    if (crowdGoalSource === src) {
      crowdGoalSource = null;
      crowdGoalGain = null;
    }
  };
}

/** Soft-out when the trophy celebration video takes over. */
export function fadeCrowdGoalCelebration(fadeSec = CROWD_GOAL_FADE_S) {
  if (!crowdGoalGain || !crowdGoalSource) return;
  const c = getCtx();
  const now = c.currentTime;
  const fade = Math.max(0.2, fadeSec);
  const g = crowdGoalGain;
  const src = crowdGoalSource;
  const cur = Math.max(g.gain.value, 0.0001);
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(cur, now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + fade);
  window.setTimeout(() => {
    try {
      src.stop();
    } catch {
      /* already ended */
    }
    if (crowdGoalSource === src) {
      crowdGoalSource = null;
      crowdGoalGain = null;
    }
  }, fade * 1000 + 40);
}

/** Warm celebration clip with the pitch bed so net-hit never waits on decode. */
export async function preloadCrowdGoalCelebration() {
  if (buffers.has(CLIP_PATHS.crowdGoal)) return;
  await loadBuffer(CLIP_PATHS.crowdGoal);
}

/**
 * Dressing-room light snap — full `dressing room light.mp3` once, loud + bass-heavy.
 * Prefer `scheduleLockerLightsAfterStart()` so it hits 4.1s desktop / 2.6s phone.
 */
export function playLockerLights(opts: { gain?: number } = {}) {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  if (lockerLightPlayed) return;

  ensureBuses();
  const url = CLIP_PATHS.lockerLight;
  const buf = buffers.get(url);
  if (!buf) {
    void loadBuffer(url).then((b) => {
      if (!b || useGameStore.getState().muted) return;
      if (lockerLightPlayed) return;
      playLockerLights(opts);
    });
    return;
  }

  lockerLightPlayed = true;
  const c = getCtx();
  const src = c.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = 1;

  // Low-shelf bass boost for heavy impact
  const bass = c.createBiquadFilter();
  bass.type = 'lowshelf';
  bass.frequency.value = 180;
  bass.gain.value = 9;

  // Mild presence bump so the snap cuts through
  const punch = c.createBiquadFilter();
  punch.type = 'peaking';
  punch.frequency.value = 110;
  punch.Q.value = 0.7;
  punch.gain.value = 5;

  const g = c.createGain();
  g.gain.value = opts.gain ?? LOCKER_LIGHT_GAIN;

  src.connect(bass);
  bass.connect(punch);
  punch.connect(g);
  g.connect(buses.ambience!);
  src.start(0);
}

/** Fire light sting once, `delayMs` after start-screen audio begins. */
export function scheduleLockerLightsAfterStart(delayMs = 3500) {
  if (lockerLightPlayed || lockerLightTimeout != null) return;
  lockerLightTimeout = window.setTimeout(() => {
    lockerLightTimeout = null;
    playLockerLights({ gain: LOCKER_LIGHT_GAIN });
  }, delayMs);
}

let popupSource: AudioBufferSourceNode | null = null;
let popupGain: GainNode | null = null;

/** Soft HUD tip sting — plays under bed audio; stop when typewriter finishes. */
export function startPopupTip(opts: { gain?: number } = {}) {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();
  stopPopupTip();

  const url = CLIP_PATHS.popup;
  const buf = buffers.get(url);
  if (!buf) {
    void loadBuffer(url).then((b) => {
      if (!b || useGameStore.getState().muted) return;
      // Avoid re-entry loop if still missing
      if (!buffers.get(url)) return;
      startPopupTip(opts);
    });
    return;
  }

  const c = getCtx();
  const src = c.createBufferSource();
  src.buffer = buf;
  const g = c.createGain();
  // Parallel with locker bed — clearly audible, still not crushing music
  g.gain.value = opts.gain ?? POPUP_TIP_GAIN;
  src.connect(g);
  g.connect(buses.ui!);
  src.onended = () => {
    if (popupSource === src) {
      popupSource = null;
      popupGain = null;
    }
  };
  src.start();
  popupSource = src;
  popupGain = g;
}

export function stopPopupTip(fadeMs = 180) {
  const src = popupSource;
  const g = popupGain;
  popupSource = null;
  popupGain = null;
  if (!src || !g) return;
  try {
    const c = getCtx();
    const now = c.currentTime;
    g.gain.cancelScheduledValues(now);
    g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + fadeMs / 1000);
    window.setTimeout(() => {
      try {
        src.stop();
      } catch {
        /* ignore */
      }
    }, fadeMs + 40);
  } catch {
    try {
      src.stop();
    } catch {
      /* ignore */
    }
  }
}

export function startCrowdBed() {
  if (!unlocked) return;
  ensureBuses();
  if (crowdSource) return;
  const url = CLIP_PATHS.crowd;
  const buf = buffers.get(url);
  const c = getCtx();
  crowdGain = c.createGain();
  crowdGain.gain.value = crowdIntensity * CROWD_GAIN_MUL;
  crowdGain.connect(buses.ambience!);

  if (buf) {
    const src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.connect(crowdGain);
    src.start();
    crowdSource = src;
  } else {
    // Soft noise bed fallback
    const n = c.createBuffer(1, c.sampleRate * 2, c.sampleRate);
    const d = n.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.25;
    const src = c.createBufferSource();
    src.buffer = n;
    src.loop = true;
    const filter = c.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 400;
    src.connect(filter);
    filter.connect(crowdGain);
    src.start();
    crowdSource = src;
  }
}

export function stopCrowdBed() {
  try {
    crowdSource?.stop();
  } catch {
    /* ignore */
  }
  crowdSource = null;
  crowdGain = null;
  crowdFilter = null;
  crowdPanner = null;
}

/**
 * Start both custom beds together:
 * - game-intro.mp3 — once only (loading sting)
 * - locker-bed.mp3 — until dressing-room door closes / tunnel entry
 * - dressing room light.mp3 — full clip; 4.1s desktop / 2.6s phone after intro bed
 */
export async function startIntroBed() {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();

  const c = getCtx();
  const now = c.currentTime;

  // Locker / till-tunnel bed — keep running if already up
  if (!lockerBedSource || lockerBedStopping) {
    const bedUrl = CLIP_PATHS.lockerBed;
    const bedBuf = buffers.get(bedUrl) ?? (await loadBuffer(bedUrl));
    if (bedBuf) {
      if (lockerBedSource) {
        try {
          lockerBedSource.stop();
        } catch {
          /* ignore */
        }
        lockerBedSource = null;
        lockerBedGain = null;
      }
      lockerBedStopping = false;
      lockerBedGain = c.createGain();
      lockerBedGain.gain.value = 0.0001;
      lockerBedGain.connect(buses.ambience!);
      const bedSrc = c.createBufferSource();
      bedSrc.buffer = bedBuf;
      bedSrc.loop = true;
      bedSrc.connect(lockerBedGain);
      bedSrc.start();
      lockerBedSource = bedSrc;
      lockerBedGain.gain.exponentialRampToValueAtTime(LOCKER_BED_GAIN, now + 1.0);
    }
  }

  // Lights-on sting — desktop 4.1s; phone 1.5s earlier (2.6s)
  scheduleLockerLightsAfterStart(computeIsMobile() ? 2600 : 4100);

  // Game intro sting — play exactly once, no loop
  if (!gameIntroPlayed) {
    const introUrl = CLIP_PATHS.gameIntro;
    const introBuf = buffers.get(introUrl) ?? (await loadBuffer(introUrl));
    if (introBuf) {
      gameIntroPlayed = true;
      gameIntroGain = c.createGain();
      gameIntroGain.gain.value = 0.0001;
      gameIntroGain.connect(buses.ambience!);
      const introSrc = c.createBufferSource();
      introSrc.buffer = introBuf;
      introSrc.loop = false;
      introSrc.connect(gameIntroGain);
      introSrc.onended = () => {
        if (gameIntroSource === introSrc) {
          gameIntroSource = null;
          gameIntroGain = null;
        }
      };
      introSrc.start();
      gameIntroSource = introSrc;
      gameIntroGain.gain.exponentialRampToValueAtTime(GAME_INTRO_GAIN, now + 0.25);
    }
  }
}

/** Fade out locker bed when dressing-room door closes / tunnel starts. */
export function stopIntroBed(fadeSec = 2.4) {
  const c = getCtx();
  const now = c.currentTime;

  if (gameIntroGain && gameIntroSource) {
    const ig = gameIntroGain;
    const isrc = gameIntroSource;
    ig.gain.cancelScheduledValues(now);
    ig.gain.setValueAtTime(Math.max(ig.gain.value, 0.0001), now);
    ig.gain.exponentialRampToValueAtTime(0.0001, now + Math.min(fadeSec, 1.6));
    window.setTimeout(() => {
      try {
        isrc.stop();
      } catch {
        /* ignore */
      }
      if (gameIntroSource === isrc) {
        gameIntroSource = null;
        gameIntroGain = null;
      }
    }, Math.min(fadeSec, 1.6) * 1000 + 60);
  }

  if (!lockerBedGain || !lockerBedSource) return;
  lockerBedStopping = true;
  const g = lockerBedGain;
  const src = lockerBedSource;
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + fadeSec);
  window.setTimeout(() => {
    try {
      src.stop();
    } catch {
      /* ignore */
    }
    if (lockerBedSource === src) {
      lockerBedSource = null;
      lockerBedGain = null;
    }
    lockerBedStopping = false;
  }, fadeSec * 1000 + 80);
}

/**
 * Custom tunnel ambience + breathing + quiet pitch under-layer.
 * Beds start silent; walk progress drives setTunnelBedIntensity(0→1).
 */
export async function startTunnelBed() {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();

  // Kill any leftover stadium / soft-noise bed from earlier phases
  stopCrowdBed();
  setCrowdIntensity(0);
  stopExitSynth(0.2);

  if (!(tunnelSource && !tunnelStopping)) {
    const url = CLIP_PATHS.tunnel;
    const buf = buffers.get(url) ?? (await loadBuffer(url));
    if (buf) {
      if (tunnelSource) {
        try {
          tunnelSource.stop();
        } catch {
          /* ignore */
        }
        tunnelSource = null;
        tunnelGain = null;
      }
      tunnelStopping = false;

      const c = getCtx();
      tunnelGain = c.createGain();
      tunnelGain.gain.value = 0.0001;
      tunnelGain.connect(buses.ambience!);

      const src = c.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      src.connect(tunnelGain);
      src.start();
      tunnelSource = src;
      tunnelBedLevel = 0;
    }
  }

  await Promise.all([startTunnelBreath(), startTunnelPitch()]);
}

/** 0 = silent tunnel beds · 1 = full (tunnel bed + quiet pitch under-layer). */
let tunnelBedLevel = 0;

export function setTunnelBedIntensity(v: number, smoothSec = 0.65) {
  tunnelBedLevel = Math.max(0, Math.min(1, v));
  if (useGameStore.getState().muted) return;
  const c = getCtx();
  const tau = Math.max(0.1, smoothSec * 0.45);
  const now = c.currentTime;
  if (tunnelGain && !tunnelStopping) {
    tunnelGain.gain.setTargetAtTime(
      Math.max(0.0001, tunnelBedLevel * TUNNEL_BED_GAIN),
      now,
      tau,
    );
  }
  if (tunnelPitchGain && !tunnelPitchStopping) {
    tunnelPitchGain.gain.setTargetAtTime(
      Math.max(0.0001, tunnelBedLevel * TUNNEL_PITCH_GAIN),
      now,
      tau,
    );
  }
}

async function startTunnelPitch() {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  if (tunnelPitchSource && !tunnelPitchStopping) return;
  ensureBuses();

  const url = CLIP_PATHS.tunnelPitch;
  const buf = buffers.get(url) ?? (await loadBuffer(url));
  if (!buf) return;

  if (tunnelPitchSource) {
    try {
      tunnelPitchSource.stop();
    } catch {
      /* ignore */
    }
    tunnelPitchSource = null;
    tunnelPitchGain = null;
  }
  tunnelPitchStopping = false;

  const c = getCtx();
  tunnelPitchGain = c.createGain();
  tunnelPitchGain.gain.value = 0.0001;
  tunnelPitchGain.connect(buses.ambience!);

  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  src.connect(tunnelPitchGain);
  src.start();
  tunnelPitchSource = src;
}

function stopTunnelPitch(fadeSec = 1.2) {
  if (!tunnelPitchGain || !tunnelPitchSource) return;
  tunnelPitchStopping = true;
  const c = getCtx();
  const now = c.currentTime;
  const g = tunnelPitchGain;
  const src = tunnelPitchSource;
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + fadeSec);
  window.setTimeout(() => {
    try {
      src.stop();
    } catch {
      /* ignore */
    }
    if (tunnelPitchSource === src) {
      tunnelPitchSource = null;
      tunnelPitchGain = null;
    }
    tunnelPitchStopping = false;
  }, fadeSec * 1000 + 80);
}

async function startTunnelBreath() {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  if (tunnelBreathSource && !tunnelBreathStopping) return;
  ensureBuses();

  const url = CLIP_PATHS.tunnelBreath;
  const buf = buffers.get(url) ?? (await loadBuffer(url));
  if (!buf) return;

  if (tunnelBreathSource) {
    try {
      tunnelBreathSource.stop();
    } catch {
      /* ignore */
    }
    tunnelBreathSource = null;
    tunnelBreathGain = null;
  }
  tunnelBreathStopping = false;

  const c = getCtx();
  tunnelBreathGain = c.createGain();
  tunnelBreathGain.gain.value = 0.0001;
  // Voice bus is hotter than ambience — max presence without soft-bed ducking
  tunnelBreathGain.connect(buses.voice!);

  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  src.connect(tunnelBreathGain);
  src.start();
  tunnelBreathSource = src;

  const now = c.currentTime;
  tunnelBreathGain.gain.exponentialRampToValueAtTime(TUNNEL_BREATH_GAIN, now + 0.8);
}

function stopTunnelBreath(fadeSec = 1.2) {
  if (!tunnelBreathGain || !tunnelBreathSource) return;
  tunnelBreathStopping = true;
  const c = getCtx();
  const now = c.currentTime;
  const g = tunnelBreathGain;
  const src = tunnelBreathSource;
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + fadeSec);
  window.setTimeout(() => {
    try {
      src.stop();
    } catch {
      /* ignore */
    }
    if (tunnelBreathSource === src) {
      tunnelBreathSource = null;
      tunnelBreathGain = null;
    }
    tunnelBreathStopping = false;
  }, fadeSec * 1000 + 80);
}

/** Fade out when leaving the tunnel for the penalty / stadium. */
export function stopTunnelBed(fadeSec = 1.6) {
  stopTunnelBreath(Math.min(fadeSec, 1.2));
  stopTunnelPitch(Math.min(fadeSec, 1.2));
  if (!tunnelGain || !tunnelSource) return;
  tunnelStopping = true;
  const c = getCtx();
  const now = c.currentTime;
  const g = tunnelGain;
  const src = tunnelSource;
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + fadeSec);
  window.setTimeout(() => {
    try {
      src.stop();
    } catch {
      /* ignore */
    }
    if (tunnelSource === src) {
      tunnelSource = null;
      tunnelGain = null;
    }
    tunnelStopping = false;
  }, fadeSec * 1000 + 80);
}

/**
 * Penalty sequence beds (parallel):
 * 1) `penalty taking bg` — music from 0.8s → 1:30 only (no loop)
 * 2) `football pitch sound` — quiet under-layer
 * netHit SFX stays independent and can fire over both.
 */
export async function preloadPitchBed() {
  if (!unlocked) return;
  await Promise.all([
    buffers.has(CLIP_PATHS.pitch)
      ? Promise.resolve()
      : loadBuffer(CLIP_PATHS.pitch),
    buffers.has(CLIP_PATHS.pitchAmbience)
      ? Promise.resolve()
      : loadBuffer(CLIP_PATHS.pitchAmbience),
    buffers.has(CLIP_PATHS.crowdGoal)
      ? Promise.resolve()
      : loadBuffer(CLIP_PATHS.crowdGoal),
    buffers.has(CLIP_PATHS.netHit)
      ? Promise.resolve()
      : loadBuffer(CLIP_PATHS.netHit),
  ]);
}

function isPitchMusicPlaying() {
  return Boolean(pitchSource && pitchGain && !pitchStopping);
}

function isPitchAmbiencePlaying() {
  return Boolean(pitchAmbienceSource && pitchAmbienceGain && !pitchAmbienceStopping);
}

/**
 * Idempotent — if music is already running, leave it alone (no cut / restart).
 * Safe to call from black handoff and again from ShotAim.
 * Plays `penalty taking bg` from 0.8s through 1:30 only.
 */
export async function startPitchBed(fadeSec = 0.9) {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();
  const rise = Math.max(0.25, fadeSec);

  // --- Music bed (once, 0.8 → 1:30) ---
  if (!isPitchMusicPlaying() && !pitchBedStartLock) {
    pitchBedStartLock = true;
    try {
      const url = CLIP_PATHS.pitch;
      const buf = buffers.get(url) ?? (await loadBuffer(url));
      // Another caller may have won the race while we awaited — never cut them
      if (buf && !isPitchMusicPlaying()) {
        pitchStopping = false;
        const c = getCtx();
        pitchGain = c.createGain();
        pitchGain.gain.value = 0.0001;
        pitchGain.connect(buses.ambience!);
        const src = c.createBufferSource();
        src.buffer = buf;
        src.loop = false;
        src.connect(pitchGain);
        const offset = Math.min(PITCH_BED_OFFSET_S, Math.max(0, buf.duration - 0.05));
        const endAt = Math.min(PITCH_BED_END_S, buf.duration);
        const playDur = Math.max(0.05, endAt - offset);
        const now = c.currentTime;
        src.start(now, offset, playDur);
        pitchSource = src;

        pitchGain.gain.exponentialRampToValueAtTime(PITCH_BED_GAIN, now + rise);
        // Soft out before the hard stop at 1:30
        const fadeOut = Math.min(1.6, playDur * 0.2);
        const fadeStart = now + Math.max(0.1, playDur - fadeOut);
        pitchGain.gain.setValueAtTime(PITCH_BED_GAIN, fadeStart);
        pitchGain.gain.exponentialRampToValueAtTime(0.0001, fadeStart + fadeOut);

        src.onended = () => {
          if (pitchSource === src) {
            pitchSource = null;
            pitchGain = null;
          }
          pitchStopping = false;
        };
      }
    } finally {
      pitchBedStartLock = false;
    }
  }

  // --- Soft pitch ambience under the music (parallel, also once) ---
  void startPitchAmbienceBed(rise + 0.25);
}

async function startPitchAmbienceBed(fadeSec = 1.1) {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  if (isPitchAmbiencePlaying() || pitchAmbienceStartLock) return;
  pitchAmbienceStartLock = true;
  try {
    ensureBuses();
    const url = CLIP_PATHS.pitchAmbience;
    const buf = buffers.get(url) ?? (await loadBuffer(url));
    if (!buf || isPitchAmbiencePlaying()) return;

    pitchAmbienceStopping = false;
    const c = getCtx();
    pitchAmbienceGain = c.createGain();
    pitchAmbienceGain.gain.value = 0.0001;
    pitchAmbienceGain.connect(buses.ambience!);

    const src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.playbackRate.value = PITCH_AMBIENCE_RATE;
    src.connect(pitchAmbienceGain);
    src.start();
    pitchAmbienceSource = src;

    const now = c.currentTime;
    pitchAmbienceGain.gain.exponentialRampToValueAtTime(
      PITCH_AMBIENCE_GAIN,
      now + Math.max(0.35, fadeSec),
    );
  } finally {
    pitchAmbienceStartLock = false;
  }
}

function stopPitchAmbienceBed(fadeSec = 1.4) {
  if (!pitchAmbienceGain || !pitchAmbienceSource) return;
  pitchAmbienceStopping = true;
  const c = getCtx();
  const now = c.currentTime;
  const g = pitchAmbienceGain;
  const src = pitchAmbienceSource;
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + fadeSec);
  window.setTimeout(() => {
    try {
      src.stop();
    } catch {
      /* ignore */
    }
    if (pitchAmbienceSource === src) {
      pitchAmbienceSource = null;
      pitchAmbienceGain = null;
    }
    pitchAmbienceStopping = false;
  }, fadeSec * 1000 + 80);
}

/** Decode the exit VO during the tunnel walk so black-screen play is instant. */
export function preloadExitBlackVoiceover() {
  const url = CLIP_PATHS.exitBlackVo;
  if (buffers.get(url)) return;
  void loadBuffer(url);
}

/** Tunnel-exit black monologue VO — plays once when the screen goes black. */
export async function playExitBlackVoiceover(opts: { gain?: number } = {}) {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();
  stopExitBlackVoiceover(0.05);

  const url = CLIP_PATHS.exitBlackVo;
  const buf = buffers.get(url) ?? (await loadBuffer(url));
  if (!buf) return;

  const c = getCtx();
  const g = c.createGain();
  g.gain.value = 0.0001;
  g.connect(buses.voice!);

  const src = c.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = 1;
  src.connect(g);
  const now = c.currentTime;
  g.gain.exponentialRampToValueAtTime(opts.gain ?? 1.15, now + 0.12);
  src.start(now);
  exitBlackVoSource = src;
  exitBlackVoGain = g;
  src.onended = () => {
    if (exitBlackVoSource === src) {
      exitBlackVoSource = null;
      exitBlackVoGain = null;
    }
  };
}

export function stopExitBlackVoiceover(fadeSec = 0.25) {
  if (!exitBlackVoGain || !exitBlackVoSource) return;
  const c = getCtx();
  const now = c.currentTime;
  const g = exitBlackVoGain;
  const src = exitBlackVoSource;
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + fadeSec);
  window.setTimeout(() => {
    try {
      src.stop();
    } catch {
      /* */
    }
    if (exitBlackVoSource === src) {
      exitBlackVoSource = null;
      exitBlackVoGain = null;
    }
  }, fadeSec * 1000 + 40);
}

/** Fade out when leaving the penalty / pitch sequence. */
export function stopPitchBed(fadeSec = 1.4) {
  stopPitchAmbienceBed(fadeSec);
  if (!pitchGain || !pitchSource) return;
  pitchStopping = true;
  const c = getCtx();
  const now = c.currentTime;
  const g = pitchGain;
  const src = pitchSource;
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + fadeSec);
  window.setTimeout(() => {
    try {
      src.stop();
    } catch {
      /* ignore */
    }
    if (pitchSource === src) {
      pitchSource = null;
      pitchGain = null;
    }
    pitchStopping = false;
  }, fadeSec * 1000 + 80);
}

/**
 * Keep / restore penalty BG music (e.g. through trophy celebration video).
 * Cancels an in-progress fade-out if needed.
 */
export function holdPitchBed(fadeSec = 0.35) {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();
  const rise = Math.max(0.2, fadeSec);
  if (pitchGain && pitchSource) {
    pitchStopping = false;
    const c = getCtx();
    const now = c.currentTime;
    pitchGain.gain.cancelScheduledValues(now);
    pitchGain.gain.setValueAtTime(Math.max(pitchGain.gain.value, 0.0001), now);
    pitchGain.gain.exponentialRampToValueAtTime(PITCH_BED_GAIN, now + rise);
  }
  // Keep soft pitch under-layer up too
  if (pitchAmbienceGain && pitchAmbienceSource && !pitchAmbienceStopping) {
    const c = getCtx();
    const now = c.currentTime;
    pitchAmbienceGain.gain.cancelScheduledValues(now);
    pitchAmbienceGain.gain.setValueAtTime(
      Math.max(pitchAmbienceGain.gain.value, 0.0001),
      now,
    );
    pitchAmbienceGain.gain.exponentialRampToValueAtTime(
      PITCH_AMBIENCE_GAIN,
      now + rise,
    );
  }
  if (!pitchGain || !pitchSource) {
    void startPitchBed(rise);
  }
}

/**
 * Portfolio handoff — keep BG playing on the site, stop looping so the track
 * can finish naturally (no hard cut / fade-out).
 */
export function releasePitchBedToFinish() {
  if (pitchSource) {
    try {
      pitchSource.loop = false;
      const src = pitchSource;
      src.onended = () => {
        if (pitchSource === src) {
          pitchSource = null;
          pitchGain = null;
        }
        pitchStopping = false;
      };
    } catch {
      /* ignore */
    }
  }
  if (pitchAmbienceSource) {
    try {
      pitchAmbienceSource.loop = false;
      const src = pitchAmbienceSource;
      src.onended = () => {
        if (pitchAmbienceSource === src) {
          pitchAmbienceSource = null;
          pitchAmbienceGain = null;
        }
        pitchAmbienceStopping = false;
      };
    } catch {
      /* ignore */
    }
  }
  // Hold audible levels through the portfolio
  if (pitchGain && !useGameStore.getState().muted) {
    const now = getCtx().currentTime;
    pitchGain.gain.cancelScheduledValues(now);
    pitchGain.gain.setTargetAtTime(PITCH_BED_GAIN, now, 0.25);
  }
  if (pitchAmbienceGain && !useGameStore.getState().muted) {
    const now = getCtx().currentTime;
    pitchAmbienceGain.gain.cancelScheduledValues(now);
    pitchAmbienceGain.gain.setTargetAtTime(PITCH_AMBIENCE_GAIN, now, 0.25);
  }
}

/**
 * Mechanical keyboard click for jersey name printing.
 * Short noise burst + pitched click — no external asset required.
 */
export function playTypeKey(opts: { backspace?: boolean } = {}) {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();
  const c = getCtx();
  const now = c.currentTime;
  const bus = buses.ui!;

  // Soft noise thock
  const noiseLen = Math.floor(c.sampleRate * 0.035);
  const noiseBuf = c.createBuffer(1, noiseLen, c.sampleRate);
  const data = noiseBuf.getChannelData(0);
  for (let i = 0; i < noiseLen; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (noiseLen * 0.18));
  }
  const noise = c.createBufferSource();
  noise.buffer = noiseBuf;
  const nf = c.createBiquadFilter();
  nf.type = 'bandpass';
  nf.frequency.value = opts.backspace ? 420 : 1800 + Math.random() * 900;
  nf.Q.value = 1.4;
  const ng = c.createGain();
  ng.gain.value = opts.backspace ? 0.16 : 0.2;
  noise.connect(nf);
  nf.connect(ng);
  ng.connect(bus);
  noise.start(now);

  // Pitched click
  const osc = c.createOscillator();
  const og = c.createGain();
  osc.type = 'triangle';
  const f0 = opts.backspace ? 160 : 780 + Math.random() * 420;
  osc.frequency.setValueAtTime(f0, now);
  osc.frequency.exponentialRampToValueAtTime(f0 * 0.55, now + (opts.backspace ? 0.06 : 0.04));
  og.gain.setValueAtTime(0.0001, now);
  og.gain.exponentialRampToValueAtTime(opts.backspace ? 0.15 : 0.2, now + 0.004);
  og.gain.exponentialRampToValueAtTime(0.0001, now + (opts.backspace ? 0.09 : 0.055));
  osc.connect(og);
  og.connect(bus);
  osc.start(now);
  osc.stop(now + (opts.backspace ? 0.1 : 0.07));
}

export function setCrowdIntensity(v: number, smoothSec = 0.55) {
  crowdIntensity = Math.max(0, Math.min(1, v));
  if (crowdGain) {
    crowdGain.gain.setTargetAtTime(
      crowdIntensity * CROWD_GAIN_MUL,
      getCtx().currentTime,
      Math.max(0.08, smoothSec * 0.45),
    );
  }
}

/** Stereo pan for stadium crowd beds — driven by camera yaw in PenaltyStadium. */
let crowdPanner: StereoPannerNode | null = null;

function ensureCrowdPanner() {
  if (!crowdGain) return null;
  if (crowdPanner) return crowdPanner;
  const c = getCtx();
  const p = c.createStereoPanner();
  p.pan.value = 0;
  try {
    crowdGain.disconnect();
  } catch {
    /* */
  }
  crowdGain.connect(p);
  p.connect(buses.ambience!);
  crowdPanner = p;
  return p;
}

/**
 * Map camera look yaw → subtle L/R crowd image.
 * yaw ≈ 0 looks toward −Z (goal); positive yaw looks right.
 */
export function updateStadiumCrowdSpatial(yaw: number) {
  try {
    const p = ensureCrowdPanner();
    if (!p) return;
    const pan = Math.max(-0.65, Math.min(0.65, Math.sin(yaw) * 0.85));
    p.pan.setTargetAtTime(pan, getCtx().currentTime, 0.18);
  } catch {
    /* never block the shot frame loop */
  }
}

export type CrowdProfile =
  | 'tunnel'
  | 'exit'
  | 'pitch'
  | 'aim'
  | 'goal'
  | 'silence'
  | 'roar';

let crowdFilter: BiquadFilterNode | null = null;
let crowdProfile: CrowdProfile = 'tunnel';
let crowdPreMuffle = crowdIntensity;

function ensureCrowdFilter() {
  if (!crowdGain) return null;
  if (crowdFilter) return crowdFilter;
  const c = getCtx();
  const f = c.createBiquadFilter();
  f.type = 'lowpass';
  f.frequency.value = 900;
  f.Q.value = 0.7;
  // Rebuild: source → filter → gain (best-effort reconnect)
  try {
    crowdSource?.disconnect();
  } catch {
    /* */
  }
  try {
    crowdGain.disconnect();
  } catch {
    /* */
  }
  if (crowdSource) crowdSource.connect(f);
  f.connect(crowdGain);
  if (crowdPanner) {
    crowdGain.connect(crowdPanner);
    try {
      crowdPanner.disconnect();
    } catch {
      /* */
    }
    crowdPanner.connect(buses.ambience!);
  } else {
    crowdGain.connect(buses.ambience!);
  }
  crowdFilter = f;
  return f;
}

/**
 * Smarter crowd colouring:
 * tunnel = low rumble · exit = brighter/whistles · pitch = open chants
 * aim = muffled · goal = explosion · silence = kill · roar = boom
 */
export function setCrowdProfile(profile: CrowdProfile) {
  crowdProfile = profile;
  ensureBuses();
  startCrowdBed();
  const f = ensureCrowdFilter();
  const now = getCtx().currentTime;
  const freq =
    profile === 'tunnel'
      ? 280
      : profile === 'exit'
        ? 1400
        : profile === 'pitch'
          ? 2200
          : profile === 'aim'
            ? 420
            : profile === 'goal' || profile === 'roar'
              ? 4500
              : 180;
  f?.frequency.setTargetAtTime(freq, now, 0.25);

  if (profile === 'silence') {
    crowdPreMuffle = crowdIntensity;
    setCrowdIntensity(0.02);
    return;
  }
  if (profile === 'roar') {
    setCrowdIntensity(1);
    play('cheer', { gain: 1.25 });
    return;
  }
  if (profile === 'aim') {
    crowdPreMuffle = Math.max(crowdIntensity, 0.35);
    setCrowdIntensity(0.18);
    return;
  }
  if (profile === 'goal') {
    setCrowdIntensity(1);
    play('goal', { gain: 1.1 });
    play('cheer', { gain: 1.05 });
    return;
  }
  // tunnel / exit / pitch — colour the bed; intensity is driven elsewhere
  if (profile === 'pitch' && crowdIntensity < 0.55) setCrowdIntensity(0.85);
}

/** Kill music beds for Moment of Silence — wind only. */
export function killMusicForSilence() {
  stopPitchBed(0.25);
  stopIntroBed(0.25);
  stopTunnelBed(0.2);
  setCrowdProfile('silence');
}

/** Soft identity boom when ADARSH lands. */
export function playIdentityBoom() {
  if (!unlocked || useGameStore.getState().muted) return;
  ensureBuses();
  const c = getCtx();
  const now = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  const f = c.createBiquadFilter();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(55, now);
  osc.frequency.exponentialRampToValueAtTime(28, now + 0.55);
  f.type = 'lowpass';
  f.frequency.value = 180;
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(0.22, now + 0.04);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
  osc.connect(f);
  f.connect(g);
  g.connect(buses.ambience!);
  osc.start(now);
  osc.stop(now + 0.75);
  play('goal', { gain: 0.55 });
}

/** Celebration theme bed — crossfades from crowd, never hard-stops. */
let celeThemeOsc: OscillatorNode | null = null;
let celeThemeGain: GainNode | null = null;
let portfolioBedOsc: OscillatorNode | null = null;
let portfolioBedGain: GainNode | null = null;

export function startCelebrationTheme() {
  if (!unlocked || useGameStore.getState().muted) return;
  ensureBuses();
  const c = getCtx();
  // Duck crowd into theme rather than killing music
  setCrowdIntensity(Math.min(crowdIntensity, 0.35));
  if (celeThemeOsc) {
    celeThemeGain?.gain.setTargetAtTime(0.05, c.currentTime, 0.4);
    return;
  }
  const osc = c.createOscillator();
  const g = c.createGain();
  const f = c.createBiquadFilter();
  osc.type = 'triangle';
  osc.frequency.value = 110;
  f.type = 'lowpass';
  f.frequency.value = 600;
  g.gain.value = 0.0001;
  g.gain.setTargetAtTime(0.05, c.currentTime, 0.8);
  osc.connect(f);
  f.connect(g);
  g.connect(buses.ambience!);
  osc.start();
  celeThemeOsc = osc;
  celeThemeGain = g;
}

export function crossfadeToPortfolioAmbience() {
  if (!unlocked || useGameStore.getState().muted) return;
  ensureBuses();
  const c = getCtx();
  const now = c.currentTime;
  // Fade celebration theme out
  if (celeThemeGain) {
    celeThemeGain.gain.setTargetAtTime(0.0001, now, 0.9);
  }
  setCrowdIntensity(0.08);
  if (portfolioBedOsc) {
    portfolioBedGain?.gain.setTargetAtTime(0.035, now, 1.0);
    return;
  }
  const osc = c.createOscillator();
  const g = c.createGain();
  const f = c.createBiquadFilter();
  osc.type = 'sine';
  osc.frequency.value = 196;
  f.type = 'bandpass';
  f.frequency.value = 480;
  f.Q.value = 0.6;
  g.gain.value = 0.0001;
  g.gain.setTargetAtTime(0.035, now, 1.2);
  osc.connect(f);
  f.connect(g);
  g.connect(buses.ambience!);
  osc.start();
  portfolioBedOsc = osc;
  portfolioBedGain = g;
  // Stop cele osc after crossfade
  const cele = celeThemeOsc;
  window.setTimeout(() => {
    try {
      cele?.stop();
    } catch {
      /* */
    }
    if (celeThemeOsc === cele) {
      celeThemeOsc = null;
      celeThemeGain = null;
    }
  }, 2200);
}

/**
 * Tunnel bed out at black handoff.
 * Does NOT stop penalty BG music — that starts with the exit VO and runs through celebration.
 */
export function silenceAfterTunnelExit(fadeSec = 0.65) {
  stopHeartbeatLoop();
  stopWindBed();
  stopExitSynth(fadeSec);
  // Longer tunnel fade so handoff doesn't hard-cut into black
  stopTunnelBed(Math.max(fadeSec, 0.7));
  stopIntroBed(fadeSec);
  stopCrowdBed();
  setCrowdIntensity(0);
  crowdProfile = 'silence';

  const c = unlocked ? getCtx() : null;
  const now = c?.currentTime ?? 0;
  if (celeThemeGain) {
    celeThemeGain.gain.setTargetAtTime(0.0001, now, 0.15);
  }
  if (portfolioBedGain) {
    portfolioBedGain.gain.setTargetAtTime(0.0001, now, 0.15);
  }
  const cele = celeThemeOsc;
  const port = portfolioBedOsc;
  window.setTimeout(() => {
    try {
      cele?.stop();
    } catch {
      /* */
    }
    try {
      port?.stop();
    } catch {
      /* */
    }
    if (celeThemeOsc === cele) {
      celeThemeOsc = null;
      celeThemeGain = null;
    }
    if (portfolioBedOsc === port) {
      portfolioBedOsc = null;
      portfolioBedGain = null;
    }
  }, fadeSec * 1000 + 100);
}

/** Dry grass footstep — no concrete echo. */
export function playGrassFootstep(sprint = false) {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();
  const c = getCtx();
  const now = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  const bp = c.createBiquadFilter();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(sprint ? 190 : 150, now);
  osc.frequency.exponentialRampToValueAtTime(70, now + 0.07);
  bp.type = 'bandpass';
  bp.frequency.value = 520;
  bp.Q.value = 0.9;
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(sprint ? 0.09 : 0.06, now + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.09);
  osc.connect(bp);
  bp.connect(g);
  g.connect(buses.foley!);
  osc.start(now);
  osc.stop(now + 0.1);
  // Soft dry turf sample on top (no delay reverb)
  play('footstep', { gain: sprint ? 0.55 : 0.38, rate: sprint ? 1.05 : 0.92 });
}

/** Duck / restore locker + tunnel + pitch beds when mute toggles. */
export function setIntroMuted(muted: boolean) {
  const c = getCtx();
  const now = c.currentTime;
  if (lockerBedGain && !lockerBedStopping) {
    lockerBedGain.gain.setTargetAtTime(muted ? 0.0001 : LOCKER_BED_GAIN, now, 0.2);
  }
  if (gameIntroGain) {
    gameIntroGain.gain.setTargetAtTime(muted ? 0.0001 : GAME_INTRO_GAIN, now, 0.2);
  }
  if (tunnelGain && !tunnelStopping) {
    tunnelGain.gain.setTargetAtTime(
      muted ? 0.0001 : Math.max(0.0001, tunnelBedLevel * TUNNEL_BED_GAIN),
      now,
      0.2,
    );
  }
  if (tunnelPitchGain && !tunnelPitchStopping) {
    tunnelPitchGain.gain.setTargetAtTime(
      muted ? 0.0001 : Math.max(0.0001, tunnelBedLevel * TUNNEL_PITCH_GAIN),
      now,
      0.2,
    );
  }
  if (tunnelBreathGain && !tunnelBreathStopping) {
    tunnelBreathGain.gain.setTargetAtTime(
      muted ? 0.0001 : TUNNEL_BREATH_GAIN,
      now,
      0.2,
    );
  }
  if (pitchGain && !pitchStopping) {
    pitchGain.gain.setTargetAtTime(muted ? 0.0001 : PITCH_BED_GAIN, now, 0.2);
  }
  if (pitchAmbienceGain && !pitchAmbienceStopping) {
    pitchAmbienceGain.gain.setTargetAtTime(
      muted ? 0.0001 : PITCH_AMBIENCE_GAIN,
      now,
      0.25,
    );
  }
  if (exitBlackVoGain) {
    exitBlackVoGain.gain.setTargetAtTime(muted ? 0.0001 : 1.15, now, 0.15);
  }
}

/** Soft whoosh only — no generated speech VO. */
export function playAnnouncerCue() {
  play('whoosh', { gain: 0.7 });
}

/** Tunnel footstep with short concrete reverb — ambience only, no music. */
export function playTunnelFootstep(sprint = false) {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();
  const c = getCtx();
  const now = c.currentTime;

  const tap = (t0: number, gain: number, freq: number) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    const bp = c.createBiquadFilter();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, t0);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.45, t0 + 0.08);
    bp.type = 'lowpass';
    bp.frequency.value = 900;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.12);
    osc.connect(bp);
    bp.connect(g);
    g.connect(buses.foley!);
    osc.start(t0);
    osc.stop(t0 + 0.14);

    // Small reverb tap
    const delay = c.createDelay(0.25);
    delay.delayTime.value = 0.055;
    const dg = c.createGain();
    dg.gain.value = gain * 0.35;
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 700;
    g.connect(delay);
    delay.connect(lp);
    lp.connect(dg);
    dg.connect(buses.foley!);
  };

  const g0 = sprint ? 0.16 : 0.11;
  tap(now, g0, sprint ? 140 : 110);
  // Also try clip if available
  play('footstep', { gain: sprint ? 0.48 : 0.32, rate: sprint ? 1.05 : 0.9 });
}

/** Short electronic “data unlock” chirp when a tunnel skill board unlocks. */
export function playDataUnlock() {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();
  const c = getCtx();
  const now = c.currentTime;
  const blip = (freq: number, t0: number, dur: number, gain = 0.16) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    const f = c.createBiquadFilter();
    osc.type = 'square';
    osc.frequency.setValueAtTime(freq, t0);
    osc.frequency.exponentialRampToValueAtTime(freq * 1.6, t0 + dur);
    f.type = 'bandpass';
    f.frequency.value = freq * 1.2;
    f.Q.value = 4;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(f);
    f.connect(g);
    g.connect(buses.ui!);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  };
  blip(880, now, 0.08, 0.14);
  blip(1320, now + 0.07, 0.1, 0.18);
  blip(1760, now + 0.15, 0.14, 0.12);
}

/** Soft heartbeat when stamina pressure rises (synth). */
export function playHeartbeat() {
  if (!unlocked) return;
  if (useGameStore.getState().muted) return;
  ensureBuses();
  const c = getCtx();
  const now = c.currentTime;
  const pulse = (t0: number) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(55, t0);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.12, t0 + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
    osc.connect(g);
    g.connect(buses.foley!);
    osc.start(t0);
    osc.stop(t0 + 0.2);
  };
  pulse(now);
  pulse(now + 0.22);
}

/** Aim-mode repeating heartbeat (~1 Hz). */
let heartbeatTimer: number | null = null;
export function startHeartbeatLoop() {
  stopHeartbeatLoop();
  playHeartbeat();
  heartbeatTimer = window.setInterval(() => playHeartbeat(), 1000);
}
export function stopHeartbeatLoop() {
  if (heartbeatTimer != null) {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

/** Quiet deep synth at tunnel mouth — Layer anticipation. */
let exitSynthOsc: OscillatorNode | null = null;
let exitSynthGain: GainNode | null = null;
export function startExitSynth() {
  if (!unlocked || useGameStore.getState().muted) return;
  if (exitSynthOsc) return;
  ensureBuses();
  const c = getCtx();
  const osc = c.createOscillator();
  const g = c.createGain();
  const f = c.createBiquadFilter();
  osc.type = 'sine';
  osc.frequency.value = 42;
  f.type = 'lowpass';
  f.frequency.value = 120;
  g.gain.value = 0.0001;
  g.gain.setTargetAtTime(0.045, c.currentTime, 0.8);
  osc.connect(f);
  f.connect(g);
  g.connect(buses.ambience!);
  osc.start();
  exitSynthOsc = osc;
  exitSynthGain = g;
}
export function stopExitSynth(fade = 0.5) {
  if (!exitSynthGain || !exitSynthOsc) return;
  const c = getCtx();
  exitSynthGain.gain.setTargetAtTime(0.0001, c.currentTime, fade * 0.3);
  const osc = exitSynthOsc;
  window.setTimeout(() => {
    try {
      osc.stop();
    } catch {
      /* */
    }
  }, fade * 1000 + 200);
  exitSynthOsc = null;
  exitSynthGain = null;
}

/** Soft wind bed for stadium cinematic. */
let windOsc: OscillatorNode | null = null;
let windGain: GainNode | null = null;
export function startWindBed(gain = 0.03) {
  if (!unlocked || useGameStore.getState().muted) return;
  if (windOsc) {
    windGain?.gain.setTargetAtTime(gain, getCtx().currentTime, 0.4);
    return;
  }
  ensureBuses();
  const c = getCtx();
  const osc = c.createOscillator();
  const g = c.createGain();
  const f = c.createBiquadFilter();
  osc.type = 'sawtooth';
  osc.frequency.value = 80;
  f.type = 'bandpass';
  f.frequency.value = 400;
  f.Q.value = 0.4;
  g.gain.value = 0.0001;
  g.gain.setTargetAtTime(gain, c.currentTime, 0.6);
  osc.connect(f);
  f.connect(g);
  g.connect(buses.ambience!);
  osc.start();
  windOsc = osc;
  windGain = g;
}
export function stopWindBed() {
  if (!windGain || !windOsc) return;
  const c = getCtx();
  windGain.gain.setTargetAtTime(0.0001, c.currentTime, 0.3);
  const osc = windOsc;
  window.setTimeout(() => {
    try {
      osc.stop();
    } catch {
      /* */
    }
  }, 800);
  windOsc = null;
  windGain = null;
}

/** Muffle crowd for aim pressure (lowpass feel via intensity duck). */
export function setCrowdMuffled(on: boolean) {
  if (on) {
    crowdPreMuffle = Math.max(crowdIntensity, crowdPreMuffle, 0.35);
    setCrowdProfile('aim');
    return;
  }
  if (crowdProfile === 'aim' || crowdProfile === 'silence') {
    setCrowdIntensity(Math.max(0.55, crowdPreMuffle));
    setCrowdProfile('pitch');
  }
}

/** Footsteps with turf character. */
export function playFootstep(sprint = false) {
  play('footstep', { gain: sprint ? 1.05 : 0.7, rate: sprint ? 1.15 : 0.95 });
}
